// src/admin/lib/adminApi.js
import {
  getFirestore, collection, getDocs, getDoc, doc,
  updateDoc, deleteDoc, addDoc, setDoc, query, where, Timestamp,
} from 'firebase/firestore';
import { sendPasswordResetEmail } from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth } from '../../firebase-config';
import { API_BASE } from '../../lib/apiBase';
import { buildAuditEntry, buildPublicUseCasePayload } from './adminLogic';
export { toDate } from './adminLogic';

const db = () => getFirestore();

// ---- Audit ----------------------------------------------------------------
export async function logAdminAction(actor, { action, targetType, targetId, before = null, after = null }) {
  const entry = buildAuditEntry({
    actorUid: actor?.uid || null,
    actorEmail: actor?.email || null,
    action, targetType, targetId, before, after,
  });
  await addDoc(collection(db(), 'adminAudit'), { ...entry, timestamp: Timestamp.now() });
}

export async function fetchAuditLog() {
  const snap = await getDocs(collection(db(), 'adminAudit'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ---- Users ----------------------------------------------------------------
export async function fetchUsers() {
  const snap = await getDocs(collection(db(), 'users'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function fetchAllLogins() {
  // Returns flat array of { userId, timestamp } across all userLogins subcollections.
  const usersSnap = await getDocs(collection(db(), 'users'));
  const all = [];
  for (const u of usersSnap.docs) {
    const lSnap = await getDocs(collection(db(), `userLogins/${u.id}/logins`));
    lSnap.forEach((l) => all.push({ userId: u.id, timestamp: l.data().timestamp }));
  }
  return all;
}

export async function changeUserRole(actor, user, newRole) {
  await updateDoc(doc(db(), 'users', user.id), { role: newRole });
  await logAdminAction(actor, {
    action: 'user.role.change', targetType: 'user', targetId: user.id,
    before: { role: user.role }, after: { role: newRole },
  });
}

// Set a user's access entitlement directly (access is entitlement-based, not
// role-based). 'Pro' grants admin-comped Pro with no expiry; 'Free' clears the
// entitlement AND the subscription record (use to reset a test account so it can
// subscribe again). Admins may write these fields (rules allow isAdmin update).
export async function setUserEntitlement(actor, user, tier) {
  const entitlement = tier === 'Pro'
    ? { tier: 'Pro', source: 'admin', expiresAt: null, updatedAt: Timestamp.now() }
    : { tier: 'Free', source: null, expiresAt: null, updatedAt: Timestamp.now() };
  const patch = { entitlement };
  if (tier !== 'Pro') patch.subscription = null; // clear sub on downgrade/reset
  await updateDoc(doc(db(), 'users', user.id), patch);
  await logAdminAction(actor, {
    action: tier === 'Pro' ? 'user.grant_pro' : 'user.reset_free',
    targetType: 'user', targetId: user.id,
    before: { entitlement: user.entitlement || null },
    after: { entitlement },
  });
}

export async function setUserSuspended(actor, user, suspended) {
  await updateDoc(doc(db(), 'users', user.id), { suspended });
  await logAdminAction(actor, {
    action: suspended ? 'user.suspend' : 'user.unsuspend',
    targetType: 'user', targetId: user.id,
    before: { suspended: !!user.suspended }, after: { suspended },
  });
}

export async function deleteUserRecord(actor, user) {
  await deleteDoc(doc(db(), 'users', user.id));
  await logAdminAction(actor, {
    action: 'user.delete', targetType: 'user', targetId: user.id,
    before: { email: user.email, role: user.role },
  });
}

export async function sendUserPasswordReset(actor, user) {
  await sendPasswordResetEmail(auth, user.email);
  await logAdminAction(actor, {
    action: 'user.password_reset', targetType: 'user', targetId: user.id,
    after: { email: user.email },
  });
}

// ---- Pro requests ---------------------------------------------------------
export async function fetchSubmissions(pending) {
  const q = query(collection(db(), 'submissions'), where('pending', '==', pending));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// Send an in-app notification to a single user (shown in the Navbar message bell).
async function notifyUser(actor, userId, subject, body) {
  if (!userId) return;
  await addDoc(collection(db(), 'messages'), {
    subject, body, senderId: actor?.uid || null, recipientIds: [userId], readBy: [], timestamp: Timestamp.now(),
  });
}

export async function approveRequest(actor, request) {
  await updateDoc(doc(db(), 'users', request.userId), {
    role: 'Pro',
    entitlement: { tier: 'Pro', source: 'admin', expiresAt: null, updatedAt: Timestamp.now() },
  });
  await updateDoc(doc(db(), 'submissions', request.id), { pending: false, resolution: 'approved', resolvedAt: Timestamp.now() });
  await notifyUser(actor, request.userId,
    'Accesso completo attivato 🎉',
    'Abbiamo verificato il tuo acquisto: ora hai accesso a tutte le 40 carte e al mentor AI. Ricarica la dashboard per iniziare. Buon brainstorming!');
  await logAdminAction(actor, {
    action: 'request.approve', targetType: 'submission', targetId: request.id,
    after: { userId: request.userId, role: 'Pro' },
  });
}

export async function rejectRequest(actor, request, reason) {
  await updateDoc(doc(db(), 'submissions', request.id), { pending: false, resolution: 'rejected', reason: reason || '', resolvedAt: Timestamp.now() });
  await notifyUser(actor, request.userId,
    'Richiesta di accesso non approvata',
    `Non siamo riusciti ad approvare la tua richiesta di accesso.${reason ? ' Motivo: ' + reason + '.' : ''} Scrivici se pensi si tratti di un errore.`);
  await logAdminAction(actor, {
    action: 'request.reject', targetType: 'submission', targetId: request.id,
    after: { reason: reason || '' },
  });
}

// ---- Use-case archive -------------------------------------------------------
export async function fetchUseCases(status) {
  const q = query(collection(db(), 'useCaseSubmissions'), where('status', '==', status));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function approveUseCase(actor, useCase) {
  await updateDoc(doc(db(), 'useCaseSubmissions', useCase.id), { status: 'approved' });
  await setDoc(doc(db(), 'publicUseCases', useCase.id), {
    ...buildPublicUseCasePayload(useCase),
    createdAt: useCase.createdAt || Timestamp.now(),
  });
  await logAdminAction(actor, {
    action: 'useCase.approve', targetType: 'useCaseSubmission', targetId: useCase.id,
    after: { status: 'approved' },
  });
}

export async function rejectUseCase(actor, useCase) {
  await updateDoc(doc(db(), 'useCaseSubmissions', useCase.id), { status: 'rejected' });
  await logAdminAction(actor, {
    action: 'useCase.reject', targetType: 'useCaseSubmission', targetId: useCase.id,
    after: { status: 'rejected' },
  });
}

// ---- Announcements --------------------------------------------------------
export async function fetchAnnouncements() {
  const snap = await getDocs(collection(db(), 'messages'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function sendAnnouncement(actor, { subject, body, recipientIds }) {
  const ref = await addDoc(collection(db(), 'messages'), {
    subject, body, senderId: actor.uid, recipientIds, readBy: [], timestamp: Timestamp.now(),
  });
  await logAdminAction(actor, {
    action: 'announcement.send', targetType: 'message', targetId: ref.id,
    after: { subject, recipients: recipientIds.length ? recipientIds.length : 'all' },
  });
  return ref.id;
}

export async function deleteAnnouncement(actor, message) {
  await deleteDoc(doc(db(), 'messages', message.id));
  await logAdminAction(actor, {
    action: 'announcement.delete', targetType: 'message', targetId: message.id,
    before: { subject: message.subject },
  });
}

// ---- App config / settings ------------------------------------------------
const CONFIG_REF = () => doc(db(), 'appConfig', 'global');

export async function fetchAppConfig() {
  const snap = await getDoc(CONFIG_REF());
  return snap.exists() ? snap.data() : { flags: {}, proCriteria: '', defaultDeckId: '' };
}

export async function saveAppConfig(actor, config) {
  await setDoc(CONFIG_REF(), { ...config, updatedBy: actor.email, updatedAt: Timestamp.now() }, { merge: true });
  await logAdminAction(actor, {
    action: 'settings.update', targetType: 'config', targetId: 'global', after: config,
  });
}

// ---- Card deck ------------------------------------------------------------
export async function fetchCardGroups() {
  const snap = await getDocs(collection(db(), 'cardGroups'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => a.order - b.order);
}

export async function saveCardGroup(actor, group) {
  const isNew = !group.id;
  const id = group.id || group.label.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  await setDoc(doc(db(), 'cardGroups', id), {
    id, label: group.label, label_en: group.label_en || '', color: group.color, order: group.order,
    active: group.active, requiresPurchase: group.requiresPurchase,
    ...(isNew ? { createdAt: Timestamp.now() } : {}),
  }, { merge: true });
  await logAdminAction(actor, {
    action: isNew ? 'deck.group.create' : 'deck.group.update',
    targetType: 'cardGroup', targetId: id, after: group,
  });
  return id;
}

export async function deleteCardGroup(actor, groupId) {
  await deleteDoc(doc(db(), 'cardGroups', groupId));
  await logAdminAction(actor, {
    action: 'deck.group.delete', targetType: 'cardGroup', targetId: groupId,
  });
}

export async function fetchCards(groupId) {
  const q = query(collection(db(), 'cards'), where('groupId', '==', groupId));
  const snap = await getDocs(q);
  const cards = snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => a.order - b.order);
  const storage = getStorage();
  return Promise.all(cards.map(async (card) => ({
    ...card,
    image: card.image ? await getDownloadURL(ref(storage, card.image)) : card.image,
    back: card.back ? await getDownloadURL(ref(storage, card.back)) : card.back,
    image_en: card.image_en ? await getDownloadURL(ref(storage, card.image_en)) : card.image_en,
    back_en: card.back_en ? await getDownloadURL(ref(storage, card.back_en)) : card.back_en,
  })));
}

// Returns the bare Storage path (not a download URL) so Firestore keeps
// storing paths, matching what migrate_cards.py writes. The caller
// (CardForm in Deck.jsx) has no live image preview during editing — cards
// are only ever displayed via fetchCards(), which resolves paths to URLs
// for display — so there's nothing that needs the resolved URL here.
export async function uploadCardImage(file, groupId, cardId, suffix = '', langFolder = null) {
  const path = langFolder
    ? `cards/${langFolder}/${groupId}/${cardId}${suffix}.jpg`
    : `cards/${groupId}/${cardId}${suffix}.jpg`;
  const storageRef = ref(getStorage(), path);
  await uploadBytes(storageRef, file);
  return path;
}

// A resolved Firebase Storage download URL (as opposed to a bare Storage
// path like `cards/A/card-1.jpg`). fetchCards() resolves image/back to
// download URLs for display, so an editingCard's draft state can hold one
// of these even when the user didn't touch the image in this edit.
const isResolvedDownloadUrl = (v) => typeof v === 'string' && /^https?:\/\//.test(v);

export async function saveCard(actor, groupId, card) {
  const isNew = !card.id;
  const id = card.id || card.label.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const payload = {
    label: card.label, label_en: card.label_en || '', groupId,
    descrizione_breve_it: card.descrizione_breve_it || '',
    descrizione_breve_en: card.descrizione_breve_en || '',
    descrizione_lunga_it: card.descrizione_lunga_it || '',
    descrizione_lunga_en: card.descrizione_lunga_en || '',
    suggerimento: card.suggerimento || '',
    freePreview: !!card.freePreview,
    order: card.order,
  };
  // Only persist image/back when they're bare paths (freshly uploaded, or
  // untouched '' on a new card). If the draft holds an already-resolved
  // download URL, the field wasn't changed in this edit — skip it so
  // merge:true leaves Firestore's existing bare-path value untouched,
  // instead of overwriting it with a URL.
  if (!isResolvedDownloadUrl(card.image)) payload.image = card.image;
  if (!isResolvedDownloadUrl(card.back)) payload.back = card.back;
  if (!isResolvedDownloadUrl(card.image_en)) payload.image_en = card.image_en || '';
  if (!isResolvedDownloadUrl(card.back_en)) payload.back_en = card.back_en || '';
  await setDoc(doc(db(), 'cards', id), payload, { merge: true });
  await logAdminAction(actor, {
    action: isNew ? 'deck.card.create' : 'deck.card.update',
    targetType: 'card', targetId: id, after: { label: card.label, groupId },
  });
  return id;
}

export async function deleteCard(actor, card) {
  await deleteDoc(doc(db(), 'cards', card.id));
  await logAdminAction(actor, {
    action: 'deck.card.delete', targetType: 'card', targetId: card.id,
    before: { label: card.label },
  });
}

// ---- Gemini models (listed by the backend with the server key) -------------
export async function fetchGeminiModels() {
  const token = await auth.currentUser.getIdToken();
  const res = await fetch(`${API_BASE}/admin/gemini-models`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`gemini-models ${res.status}`);
  return res.json(); // { models: [{id, displayName}], default }
}
